/** Reuse the same bearer token for burst API traffic (e.g. ATC preview polling). */
export const BEARER_TOKEN_CACHE_TTL_MS = 1000; // 1 second

type CacheEntry = {
  userId: string;
  bearerToken: string;
  expiresAt: number;
};

let cacheEntry: CacheEntry | null = null;
let inFlight: Promise<string | undefined> | null = null;
let inFlightIsForced = false;

export const clearBearerTokenCache = (): void => {
  cacheEntry = null;
  inFlight = null;
  inFlightIsForced = false;
};

export const getBearerTokenWithCache = async (params: {
  userId: string | null;
  fetchBearerToken: () => Promise<string | undefined>;
  /**
   * Skip the cached token and start a fresh fetch (used on 401 retry, where
   * the cached/current token was just rejected by the server). Concurrent
   * forced calls share a single fetch.
   */
  forceRefresh?: boolean;
  now?: () => number;
}): Promise<string | undefined> => {
  const now = params.now ?? (() => Date.now());
  const forceRefresh = params.forceRefresh === true;

  if (params.userId === null) {
    clearBearerTokenCache();
    return undefined;
  }

  const userId = params.userId;
  const cached = cacheEntry;
  if (
    !forceRefresh &&
    cached !== null &&
    cached.userId === userId &&
    cached.expiresAt > now()
  ) {
    return cached.bearerToken;
  }

  if (cached !== null && (forceRefresh || cached.userId !== userId)) {
    cacheEntry = null;
  }

  // A forced call must not reuse an unforced in-flight fetch: that fetch may
  // resolve to the very token the server just rejected.
  if (inFlight !== null && (!forceRefresh || inFlightIsForced)) {
    return inFlight;
  }

  const promise = (async (): Promise<string | undefined> => {
    const bearerToken = await params.fetchBearerToken();
    if (bearerToken !== undefined) {
      cacheEntry = {
        userId,
        bearerToken,
        expiresAt: now() + BEARER_TOKEN_CACHE_TTL_MS,
      };
    } else {
      cacheEntry = null;
    }
    return bearerToken;
  })();

  inFlight = promise;
  inFlightIsForced = forceRefresh;
  try {
    return await promise;
  } finally {
    if (inFlight === promise) {
      inFlight = null;
      inFlightIsForced = false;
    }
  }
};
